import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowRight, ShieldCheck, Languages, IdCard } from "lucide-react";
import { scrollToId } from "./Reveal";
import { CONTACT, IMAGES } from "../../data";
import { useLang } from "../../i18n";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineVar = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const BADGE_ICONS = [Languages, ShieldCheck, IdCard];

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayO = useTransform(scrollYProgress, [0, 1], [0.6, 0.95]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Industrial forklift under spotlight"
          className="w-full h-full object-cover object-center"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayO }}
        className="absolute inset-0 z-0 bg-gradient-to-t from-[#060c1f] via-[#060c1f]/70 to-[#060c1f]/40"
      />
      <div className="absolute inset-0 z-0 grid-lines opacity-40" />
      <div className="absolute right-6 top-28 z-0 hidden md:block h-64 w-64 rounded-full bg-blue-600/20 blur-3xl" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <div className="flex items-center gap-1.5 bg-white/5 backdrop-blur border border-white/10 rounded-full pl-2 pr-3.5 py-1.5">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={13} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-xs text-white font-semibold">5.0</span>
            <span className="text-xs text-slate-400">· {t.hero.reviews}</span>
          </div>
          <span className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-amber-400 hidden sm:block">
            {t.hero.tagline}
          </span>
        </motion.div>

        <motion.h1
          key={t.hero.lines.join("|")}
          variants={container}
          initial="hidden"
          animate="show"
          className="font-head font-extrabold uppercase leading-[0.86] tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl"
        >
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineVar} className="block">
                {line}
              </motion.span>
            </span>
          ))}
          <span className="block overflow-hidden">
            <motion.span variants={lineVar} className="block text-gold-grad">
              {t.hero.last}
            </motion.span>
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
        >
          <button
            onClick={() => scrollToId("quote")}
            data-testid="hero-quote-btn"
            className="group inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-[#060c1f] font-head font-bold uppercase tracking-wide text-base px-7 py-4 rounded-md transition-colors"
          >
            {t.hero.cta}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-amber-400/60 hover:bg-white/5 text-white font-head font-bold uppercase tracking-wide text-base px-7 py-4 rounded-md transition-colors"
          >
            {t.hero.call} {CONTACT.phone}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
        >
          {t.hero.badges.map((text, i) => {
            const Icon = BADGE_ICONS[i];
            return (
              <div key={text} className="flex items-center gap-2 text-sm text-slate-300">
                <Icon size={16} className="text-amber-400" />
                {text}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
